import React from 'react';
import usePayment from '../../hooks/usePayment';
import { useAssessment } from '../../contexts/AssessmentContext';
import CosmicIcon from '../common/CosmicIcon';
import './UpgradePrompt.css';

/**
 * Reusable upgrade prompt component for the 3-tier freemium model
 * Displays contextual upgrade prompts based on user tier and target tier
 */
const UpgradePrompt = ({ 
  targetTier, 
  context = 'general',
  className = '',
  variant = 'card', // 'card', 'banner', 'inline'
  showFeatures = true,
  customMessage = null,
  overrideTier = null,
  onSuccess = null
}) => {
  const { 
    userTier, 
    shouldShowUpgradePrompt,
    USER_TIERS,
    TIER_PRICING 
  } = useAssessment();

  // Hooks must be top-level, before any early returns
  const { openPayment, modal } = usePayment();

  const currentTierForDisplay = overrideTier || userTier;

  // Don't show if user is already at target tier or higher
  if (currentTierForDisplay === USER_TIERS.PREMIUM || 
      (currentTierForDisplay === USER_TIERS.ENHANCED && targetTier === USER_TIERS.ENHANCED)) {
    return null;
  }

  // Don't show if upgrade prompts are disabled
  if (!shouldShowUpgradePrompt(context)) {
    return null;
  }

  const getUpgradeContent = () => {
    const isEnhancedUpgrade = targetTier === USER_TIERS.ENHANCED;
    const isPremiumUpgrade = targetTier === USER_TIERS.PREMIUM;
    
    if (customMessage) {
      return {
        title: customMessage.title,
        description: customMessage.description,
        features: customMessage.features || []
      };
    }

    if (isEnhancedUpgrade) {
      return {
        title: "Unlock Your Top 8 Motivated Elements",
        description: "Get high-precision scores and significantly better career insights with Enhanced tier",
        features: [
          "Complete Stage 2 Element Confirmation",
          "High-precision scores for your top 8 elements",
          "Behavioral distinctiveness analysis", 
          "Element interaction insights",
          "Significantly better career recommendations"
        ]
      };
    }

    if (isPremiumUpgrade) {
      return {
        title: "Complete Your Career Profile",
        description: "Professional-grade assessment with all 45 element scores and comprehensive analysis",
        features: [
          "Complete 3-Stage Assessment Framework",
          "All 45 motivated element scores",
          "Cross-domain element analysis",
          "Career-critical element assessment",
          "Professional PDF report",
          "Industry add-on included ($19.99 value)"
        ]
      };
    }

    return {
      title: "Upgrade Your Assessment",
      description: "Unlock more insights about your motivated career elements",
      features: []
    };
  };

  const content = getUpgradeContent();
  const getEffectivePrice = () => {
    const isPremiumTargetLocal = targetTier === USER_TIERS.PREMIUM;
    if (isPremiumTargetLocal && currentTierForDisplay === USER_TIERS.ENHANCED) {
      return 14.99; // upgrade delta from Enhanced to Premium
    }
    return TIER_PRICING[targetTier];
  };
  const price = getEffectivePrice();
  const tierName = targetTier === USER_TIERS.ENHANCED ? 'Enhanced' : 'Premium';

  const getContextualCTA = () => {
    switch (context) {
      case 'constellation':
        return `See your scores for ${targetTier === USER_TIERS.ENHANCED ? '5 more elements' : 'all elements'}`;
      case 'element_grid':
        return `Unlock ${targetTier === USER_TIERS.ENHANCED ? 'top 8' : 'all 45'} element scores`;
      case 'career_recommendations':
        return 'Get detailed career recommendations';
      case 'pdf_report':
        return 'Download your professional report';
      case 'results_overview':
        return `See your complete ${tierName.toLowerCase()} analysis`;
      default:
        return `Upgrade to ${tierName}`;
    }
  };

  if (variant === 'banner') {
    return (
      <div className={`upgrade-prompt upgrade-prompt--banner ${className}`}>
        <div className="upgrade-prompt__content">
          <div className="upgrade-prompt__text">
            <h4>{content.title}</h4>
            <p>{content.description}</p>
          </div>
          <button 
            className="upgrade-prompt__cta upgrade-prompt__cta--banner"
            onClick={() => openPayment({ targetTier, currentTier: currentTierForDisplay, userId: 'anonymous', sourceContext: context, onSuccess })}
          >
            {getContextualCTA()} - ${price}
          </button>
          {modal}
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`upgrade-prompt upgrade-prompt--inline ${className}`}>
        <span className="upgrade-prompt__inline-text">{content.description}</span>
        <button 
          className="upgrade-prompt__cta upgrade-prompt__cta--inline"
          onClick={() => openPayment({ targetTier, currentTier: currentTierForDisplay, userId: 'anonymous', sourceContext: context, onSuccess })}
        >
          {getContextualCTA()}
        </button>
        {modal}
      </div>
    );
  }

  // Default card variant
  return (
    <div className={`upgrade-prompt upgrade-prompt--card ${className}`}>
      <div className="upgrade-prompt__header">
        <h3 className="upgrade-prompt__title">{content.title}</h3>
        <div className="upgrade-prompt__price">
          <span className="upgrade-prompt__price-amount">${price}</span>
          <span className="upgrade-prompt__price-tier">{tierName}</span>
        </div>
      </div>
      
      <p className="upgrade-prompt__description">{content.description}</p>
      
      {showFeatures && content.features.length > 0 && (
        <ul className="upgrade-prompt__features">
          {content.features.map((feature, index) => (
            <li key={index} className="upgrade-prompt__feature">
              <span className="upgrade-prompt__feature-icon"><CosmicIcon name="ui_check" size={16} decorative /></span>
              {feature}
            </li>
          ))}
        </ul>
      )}
      
      <button 
        className="upgrade-prompt__cta upgrade-prompt__cta--primary"
        onClick={() => openPayment({ targetTier, currentTier: currentTierForDisplay, userId: 'anonymous', sourceContext: context, onSuccess })}
      >
        <span className="upgrade-icon-lock-morph" aria-hidden="true">
          <span className="icon-bolt"><CosmicIcon name="ui_bolt" size={18} decorative /></span>
          <span className="icon-key"><CosmicIcon name="ui_unlocked" size={18} decorative /></span>
        </span>
        {getContextualCTA()}
      </button>
      
      <div className="upgrade-prompt__benefits">
        <div className="upgrade-prompt__benefit">
          <span className="upgrade-prompt__benefit-icon"><CosmicIcon name="ui_science" size={18} decorative /></span>
          <span>Scientific Foundation</span>
        </div>
        <div className="upgrade-prompt__benefit">
          <span className="upgrade-prompt__benefit-icon"><CosmicIcon name="ui_bolt" size={18} decorative /></span>
          <span>Research-Backed Insights</span>
        </div>
        <div className="upgrade-prompt__benefit">
          <span className="upgrade-prompt__benefit-icon"><CosmicIcon name="ui_target" size={18} decorative /></span>
          <span>Precision Career Guidance</span>
        </div>
      </div>
      {modal}
    </div>
  );
};

export default UpgradePrompt; 
